import { useCallback, useEffect, useRef, useState } from 'react';
import { connectivity, isDown, useConnectivity } from '../api/connectivity';
import { ApiError, OfflineError, apiFetch } from '../api/http';
import { ca } from '../i18n/ca';

/** What the loading and connection components (`PageStates.tsx`) share with the pages. */

/** "les 21:40" today, "17 d’octubre a les 21:40" another day. */
export function whenText(at: number, now = Date.now()): string {
  const d = new Date(at);
  const hm = d.toLocaleTimeString('ca', { hour: '2-digit', minute: '2-digit' });
  if (new Date(now).toDateString() === d.toDateString()) return ca.connection.at(hm);
  return ca.connection.atDay(d.toLocaleDateString('ca', { day: 'numeric', month: 'long' }), hm);
}

/**
 * Ask the platform once, from the network and never from a kept copy: its
 * answer is what tells the notice the connection is back (`connectivity.ts`).
 */
export function probeConnection(): Promise<void> {
  return apiFetch('/api/meta', { cache: 'no-store' }).then(
    () => undefined,
    () => undefined,
  );
}

/**
 * Ask the platform once, and every page that reads with `useRetryKey` again:
 * at once while nothing says the connection is gone, else when the probe
 * finds it back (`useConnectionWatch`), so a page does not read twice.
 */
export function retryConnection() {
  if (!isDown(connectivity.get())) window.dispatchEvent(new Event(RETRY_EVENT));
  void probeConnection();
}

/** How often the shell asks the platform again while the connection is gone (ms). */
export const RECHECK_MS = 10_000;

/**
 * Mounted once (`App`): while the page has no connection it asks the platform
 * every `RECHECK_MS`, and when the browser says it is online it asks at once,
 * so the notice turns to «La connexió ha tornat» by itself, without waiting
 * for a press of «Torna-ho a provar» that found the phone still waking its
 * radio (web card 226, 2026-10-19: «L’avís no canvia»). When the connection
 * comes back, every page that reads with `useRetryKey` reads again.
 */
export function useConnectionWatch() {
  const down = isDown(useConnectivity());
  useEffect(() => {
    const up = () => void probeConnection();
    window.addEventListener('online', up);
    return () => window.removeEventListener('online', up);
  }, []);
  useEffect(() => {
    if (!down) return undefined;
    const timer = setInterval(() => void probeConnection(), RECHECK_MS);
    return () => clearInterval(timer);
  }, [down]);
  const wasDown = useRef(down);
  useEffect(() => {
    if (wasDown.current && !down) window.dispatchEvent(new Event(RETRY_EVENT));
    wasDown.current = down;
  }, [down]);
}

/** Fired by the notice's "try again": a page that listens reads what it shows again. */
export const RETRY_EVENT = 'ops:retry';

/** Whether a failure is the connection's, not an answer the platform gave (a proxy's gateway page is not JSON). */
export function isConnectionProblem(err: unknown): boolean {
  return (
    err instanceof OfflineError ||
    (err instanceof ApiError &&
      err.status >= 502 &&
      err.status <= 504 &&
      (err.body === null || typeof err.body === 'string'))
  );
}

/**
 * A number for a page's read effect to depend on: it moves when the page's own
 * "try again" is pressed, and when the connection notice's is.
 */
export function useRetryKey(): [number, () => void] {
  const [key, setKey] = useState(0);
  const retry = useCallback(() => setKey((k) => k + 1), []);
  useEffect(() => {
    window.addEventListener(RETRY_EVENT, retry);
    return () => window.removeEventListener(RETRY_EVENT, retry);
  }, [retry]);
  return [key, retry];
}

/** While mounted, the page draws its own connection notice and the shell's stays hidden. */
export function useOwnConnectionNotice() {
  useEffect(() => connectivity.ownNotice(), []);
}
